import React, { useState, useEffect } from 'react'
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  Tag,
  Space,
  Button,
  Tabs,
  Alert,
  Typography,
  Tooltip,
  Divider,
  Badge
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowLeft,
  faUsers,
  faExclamationTriangle,
  faCheckCircle,
  faTimesCircle,
  faCalendarCheck,
  faChartLine,
  faBell,
  faUserMd,
  faVial,
  faHeartbeat
} from '@fortawesome/free-solid-svg-icons'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadialBarChart,
  RadialBar
} from 'recharts'
import { generateMockPerformanceData, generateMockAlerts } from '../../utils/mockPerformanceData'
import { hexToRGBA } from '../../../../lib/utils'

const { Title, Text } = Typography
const { TabPane } = Tabs

const PlanPerformance = ({ plan, onBack }) => {
  const [performanceData, setPerformanceData] = useState(null)
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('1')

  useEffect(() => {
    // Simulate API call to fetch performance data
    setLoading(true)
    setTimeout(() => {
      setPerformanceData(generateMockPerformanceData(plan._id))
      setAlerts(generateMockAlerts(plan._id))
      setLoading(false)
    }, 800)
  }, [plan._id])

  // Color configurations for the dashboard
  const colors = {
    primary: '#1890ff',
    success: '#52c41a',
    warning: '#faad14',
    error: '#f5222d',
    chart: ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2'],
    riskLevels: {
      Low: '#52c41a',
      Medium: '#faad14',
      High: '#fa541c',
      Critical: '#f5222d'
    }
  }

  if (loading) {
    return (
      <Card loading={true} style={{ minHeight: 400 }}>
        <div style={{ textAlign: 'center', padding: '100px 0' }}>Loading performance metrics...</div>
      </Card>
    )
  }

  const complianceRateColor = () => {
    const rate = performanceData.overview.complianceRate
    if (rate >= 85) return colors.success
    if (rate >= 70) return colors.primary
    if (rate >= 50) return colors.warning
    return colors.error
  }

  // Format the month labels for the compliance trend chart
  const formatMonthLabels = () => {
    return performanceData.complianceTrend.map((item) => {
      const [year, month] = item.month.split('-')
      const date = new Date(year, month - 1)
      return date.toLocaleString('default', { month: 'short' })
    })
  }

  // Transform test compliance data for charts
  const testComplianceData = Object.entries(performanceData.testCompliance).map(([key, value]) => ({
    name: key
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, (str) => str.toUpperCase()),
    value,
    fill: value >= 85 ? colors.success : value >= 70 ? colors.primary : value >= 50 ? colors.warning : colors.error
  }))

  // Prepare data for visit compliance pie chart
  const visitComplianceData = [
    { name: 'Completed', value: performanceData.visitCompliance.completedVisits, fill: colors.success },
    { name: 'Missed', value: performanceData.visitCompliance.missedVisits, fill: colors.error },
    { name: 'Upcoming', value: performanceData.visitCompliance.upcomingVisits, fill: colors.primary }
  ]

  // Columns for the non-compliant patients table
  const nonCompliantColumns = [
    {
      title: 'Patient',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Last Visit',
      dataIndex: 'lastVisit',
      key: 'lastVisit',
      render: (date) => {
        const days = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24))
        return (
          <Tooltip title={`${days} days ago`}>
            <span>
              {date} {days > 90 && <Badge status='error' />}
            </span>
          </Tooltip>
        )
      },
      sorter: (a, b) => new Date(a.lastVisit) - new Date(b.lastVisit)
    },
    {
      title: 'Missed Tests',
      dataIndex: 'missedTests',
      key: 'missedTests',
      render: (tests) => (
        <Space wrap>
          {tests.map((test) => (
            <Tag color='red' key={test}>
              {test}
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Missed Visits',
      dataIndex: 'missedVisits',
      key: 'missedVisits',
      render: (visits) => (
        <Badge count={visits} style={{ backgroundColor: visits > 2 ? colors.error : colors.warning }} />
      )
    },
    {
      title: 'Risk Level',
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      render: (level) => (
        <Tag color={colors.riskLevels[level]} key={level}>
          {level.toUpperCase()}
        </Tag>
      ),
      sorter: (a, b) => {
        const levels = { Low: 1, Medium: 2, High: 3, Critical: 4 }
        return levels[a.riskLevel] - levels[b.riskLevel]
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button type='primary' size='small'>
            <FontAwesomeIcon icon={faUserMd} style={{ marginRight: 8 }} />
            Contact
          </Button>
          <Button size='small'>
            <FontAwesomeIcon icon={faCalendarCheck} style={{ marginRight: 8 }} />
            Schedule
          </Button>
        </Space>
      )
    }
  ]

  // Columns for the alerts table
  const alertsColumns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '20%'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '40%'
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => new Date(date).toLocaleDateString(),
      width: '10%'
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity) => {
        const color = severity === 'High' ? colors.error : severity === 'Medium' ? colors.warning : colors.primary
        return <Tag color={color}>{severity}</Tag>
      },
      width: '10%'
    },
    {
      title: 'Status',
      dataIndex: 'resolved',
      key: 'resolved',
      render: (resolved) => (resolved ? <Tag color='success'>Resolved</Tag> : <Tag color='error'>Active</Tag>),
      width: '10%'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button type={record.resolved ? 'default' : 'primary'} size='small' disabled={record.resolved}>
          {record.resolved ? 'Resolved' : 'Resolve'}
        </Button>
      ),
      width: '10%'
    }
  ]

  return (
    <div style={{ background: '#f0f2f5', padding: '20px', borderRadius: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Space align='center'>
          <Button
            icon={<FontAwesomeIcon icon={faArrowLeft} />}
            onClick={onBack}
            style={{
              display: 'flex',
              alignItems: 'center',
              borderColor: '#d9d9d9'
            }}
          >
            Back
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            Performance Metrics: {plan.name}
          </Title>
        </Space>
        <Text type='secondary'>Last Updated: {new Date(performanceData.overview.lastUpdated).toLocaleString()}</Text>
      </div>

      {/* Overview Cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ height: '100%' }}>
            <Statistic
              title={
                <Space>
                  <FontAwesomeIcon icon={faUsers} />
                  <span>Total Patients</span>
                </Space>
              }
              value={performanceData.overview.totalPatients}
              valueStyle={{ color: colors.primary }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ height: '100%' }}>
            <Statistic
              title={
                <Space>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Compliant Patients</span>
                </Space>
              }
              value={performanceData.overview.compliantPatients}
              valueStyle={{ color: colors.success }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ height: '100%' }}>
            <Statistic
              title={
                <Space>
                  <FontAwesomeIcon icon={faTimesCircle} />
                  <span>Non-Compliant Patients</span>
                </Space>
              }
              value={performanceData.overview.nonCompliantPatients}
              valueStyle={{ color: colors.error }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ height: '100%' }}>
            <Statistic
              title={
                <Space>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>Active Alerts</span>
                </Space>
              }
              value={performanceData.overview.alertCount}
              valueStyle={{ color: colors.warning }}
            />
          </Card>
        </Col>
      </Row>

      {/* Overall Compliance Rate */}
      <Row gutter={[16, 16]} style={{ marginTop: '20px' }}>
        <Col span={24}>
          <Card>
            <Title level={4}>Overall Plan Compliance</Title>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 0' }}>
              <Progress
                type='dashboard'
                percent={performanceData.overview.complianceRate}
                strokeColor={complianceRateColor()}
                width={200}
                strokeWidth={10}
                format={(percent) => (
                  <span style={{ fontSize: '24px', fontWeight: 'bold', color: complianceRateColor() }}>{percent}%</span>
                )}
              />
            </div>
            <div style={{ textAlign: 'center', marginTop: '10px' }}>
              <Text strong>
                {performanceData.overview.complianceRate >= 85
                  ? 'Excellent compliance rate!'
                  : performanceData.overview.complianceRate >= 70
                  ? 'Good compliance rate - continue monitoring'
                  : performanceData.overview.complianceRate >= 50
                  ? 'Moderate compliance - needs improvement'
                  : 'Poor compliance - urgent attention required'}
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Tabs for detailed metrics */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginTop: '20px' }}
        tabBarStyle={{ marginBottom: '16px', background: '#fff', padding: '8px 16px', borderRadius: '4px' }}
      >
        <TabPane
          tab={
            <span>
              <FontAwesomeIcon icon={faChartLine} style={{ marginRight: 8 }} />
              Compliance Trends
            </span>
          }
          key='1'
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card>
                <Title level={4}>Monthly Compliance Trend</Title>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <LineChart data={performanceData.complianceTrend}>
                      <CartesianGrid strokeDasharray='3 3' stroke='#eee' />
                      <XAxis
                        dataKey='month'
                        tickFormatter={(value) => {
                          const [year, month] = value.split('-')
                          const date = new Date(year, month - 1)
                          return date.toLocaleString('default', { month: 'short' })
                        }}
                      />
                      <YAxis domain={[0, 100]} />
                      <RechartsTooltip
                        formatter={(value, name) => [`${value}%`, name === 'compliance' ? 'Compliance' : 'Target']}
                        labelFormatter={(label) => {
                          const [year, month] = label.split('-')
                          const date = new Date(year, month - 1)
                          return date.toLocaleString('default', { month: 'long', year: 'numeric' })
                        }}
                      />
                      <Legend />
                      <Line
                        type='monotone'
                        dataKey='compliance'
                        stroke={colors.primary}
                        strokeWidth={3}
                        activeDot={{ r: 8 }}
                        name='Compliance'
                      />
                      <Line
                        type='monotone'
                        dataKey='target'
                        stroke={colors.success}
                        strokeDasharray='5 5'
                        strokeWidth={2}
                        name='Target'
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
            <Col xs={24} lg={12}>
              <Card style={{ height: '100%' }}>
                <Title level={4}>Test Type Compliance</Title>
                <div style={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <BarChart
                      data={testComplianceData}
                      margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
                      layout='vertical'
                    >
                      <CartesianGrid strokeDasharray='3 3' horizontal={true} vertical={false} />
                      <XAxis type='number' domain={[0, 100]} />
                      <YAxis type='category' dataKey='name' width={150} />
                      <RechartsTooltip formatter={(value) => [`${value}%`, 'Compliance']} />
                      <Bar dataKey='value' nameKey='name' radius={[0, 4, 4, 0]}>
                        {testComplianceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card style={{ height: '100%' }}>
                <Title level={4}>Visit Compliance</Title>
                <div style={{ width: '100%', height: 300, display: 'flex', justifyContent: 'center' }}>
                  <ResponsiveContainer width='80%' height='100%'>
                    <PieChart>
                      <Pie
                        data={visitComplianceData}
                        dataKey='value'
                        nameKey='name'
                        cx='50%'
                        cy='50%'
                        outerRadius={100}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={true}
                      >
                        {visitComplianceData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(value, name) => [value, name]} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </Col>
          </Row>
        </TabPane>

        <TabPane
          tab={
            <span>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 8 }} />
              Non-Compliant Patients
            </span>
          }
          key='2'
        >
          <Card>
            <Title level={4}>Patients Requiring Attention</Title>
            <Alert
              message='Patient Compliance Issues Detected'
              description={`${performanceData.nonCompliantPatients.length} patients are non-compliant with their care plan requirements. These patients may require follow-up.`}
              type='warning'
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Table
              columns={nonCompliantColumns}
              dataSource={performanceData.nonCompliantPatients}
              rowKey='id'
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </TabPane>

        <TabPane
          tab={
            <span>
              <FontAwesomeIcon icon={faBell} style={{ marginRight: 8 }} />
              Alerts
            </span>
          }
          key='3'
        >
          <Card>
            <Title level={4}>System Alerts</Title>
            <Alert
              message='Active Alerts Require Attention'
              description={`There are ${
                alerts.filter((a) => !a.resolved).length
              } active alerts that require attention.`}
              type='warning'
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Table columns={alertsColumns} dataSource={alerts} rowKey='id' pagination={{ pageSize: 5 }} />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  )
}

export default PlanPerformance
